import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { Fetcher, HTTPError } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Link } from '@this/components/shared/ui/navigations/link';
import {
  OrganizationBody,
  OrganizationTitle,
  OrganizationNavBar
} from '@this/components/organization/organization.style';
import InvoiceCustomFormatForm from '@this/components/organization/invoice_custom_formats/invoice_custom_format_form';
import InvoiceCustomFormat from '@this/domain/invoice_custom_format/invoice_custom_format';
import type { InvoiceCustomFormatArgs } from '@this/domain/invoice_custom_format/invoice_custom_format';
import InvoiceStandardTable from '@this/src/domain/invoice_custom_format/invoice_standard_table';
import type { InvoiceStandardTableArgs } from '@this/src/domain/invoice_custom_format/invoice_standard_table';

interface Response {
  invoice_custom_format: InvoiceCustomFormatArgs;
  invoice_standard_tables: InvoiceStandardTableArgs[];
}

const InvoiceCustomFormatEdit = observer(() => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceCustomFormat, setInvoiceCustomFormat] = useState<InvoiceCustomFormat | null>(null);
  const [invoiceStandardTables, setInvoiceStandardTables] = useState<InvoiceStandardTable[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const fetchInvoiceCustomFormat = async () => {
    setLoading(true);
    const response = await Fetcher.get<Response>(`/organization/invoice_custom_formats/${id}/edit.json`);
    setInvoiceCustomFormat(new InvoiceCustomFormat(response.invoice_custom_format));
    setInvoiceStandardTables(response.invoice_standard_tables.map(t => new InvoiceStandardTable(t)));
    setLoading(false);
  };

  useEffect(() => {
    fetchInvoiceCustomFormat();
  }, []);

  const handleEdit = async () => {
    if (invoiceCustomFormat === null) return;
    setSaving(true);
    setErrors([]);
    try {
      await Fetcher.put(`/organization/invoice_custom_formats/${id}.json`, invoiceCustomFormat.submitParams());
      history.push('/organization/invoice_custom_formats');
    } catch (e) {
      if (e instanceof HTTPError && e.response?.data) {
        const responseError = e.response.data.errors ?? e.response.data.error ?? e.response.data.message;
        const submitErrors = Array.isArray(responseError)
          ? responseError
          : responseError
          ? [String(responseError)]
          : [];
        setErrors(submitErrors);
      } else {
        setErrors(['通信環境が不安定です。\n時間をおいてもう一度お試しください。']);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <OrganizationNavBar>
        <Link href="/organization/invoice_custom_formats" style={{ display: 'flex', alignItems: 'center' }}>
          <ChevronLeft />
          カスタムCSVに戻る
        </Link>
      </OrganizationNavBar>
      <OrganizationTitle>カスタムCSV編集</OrganizationTitle>
      <OrganizationBody>
        {errors.length > 0 && (
          <Alert variant="danger" onClose={() => setErrors([])} style={{ marginBottom: '16px' }}>
            {errors.map(error => (
              <div key={error}>{error}</div>
            ))}
          </Alert>
        )}
        {loading ? (
          <Loading />
        ) : (
          invoiceCustomFormat && (
            <InvoiceCustomFormatForm
              invoiceCustomFormat={invoiceCustomFormat}
              invoiceStandardTables={invoiceStandardTables}
              onSubmit={handleEdit}
              saving={saving}
            />
          )
        )}
      </OrganizationBody>
    </>
  );
});

export default InvoiceCustomFormatEdit;
